import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types/dashboard';
import { authService } from '../services/authService';
import { mapBackendUserToFrontend } from '../utils/userMapper';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Token expiry tracking (internal)
  _tokenExpiry: number | null;
  _refreshTokenExpiry: number | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  checkTokenExpiry: () => void;
  clearError: () => void;
  updateUser: (updates: Partial<User>) => void;
}

// Global timer reference for token refresh
let refreshTimer: NodeJS.Timeout | null = null;

/**
 * Authentication Store
 * Single source of truth for authentication state
 * - Zustand persist: Stores user, auth status, and token expiry
 * - localStorage: Stores access token, refresh token (for httpClient access)
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      _tokenExpiry: null,
      _refreshTokenExpiry: null,

      /**
       * Login with email and password
       * Calls real backend API endpoint
       */
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.login(email, password);
          const user = mapBackendUserToFrontend(response.user);

          // Store tokens in localStorage (httpClient needs 'authToken' key)
          localStorage.setItem('authToken', response.accessToken);
          localStorage.setItem('refreshToken', response.refreshToken);
          localStorage.setItem('tokenExpiry', response.accessTokenExpiry.toString());

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            _tokenExpiry: response.accessTokenExpiry,
            _refreshTokenExpiry: response.refreshTokenExpiry
          });

          // Start proactive token refresh timer
          get().checkTokenExpiry();
        } catch (error: any) {
          // Clear any partial authentication state
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('tokenExpiry');

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message || 'Login failed',
            _tokenExpiry: null,
            _refreshTokenExpiry: null
          });

          throw error; // Re-throw for caller to handle
        }
      },

      /**
       * Logout user
       * Calls backend logout API then clears all state
       * Ensures cleanup even if API call fails
       */
      logout: async () => {
        set({ isLoading: true });

        try {
          await authService.logout();
        } catch (error) {
          // Log error but continue with local cleanup
          console.error('Logout API call failed:', error);
        } finally {
          // Always clear local state (even if API fails)
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('tokenExpiry');

          // Clear timer
          if (refreshTimer) {
            clearTimeout(refreshTimer);
            refreshTimer = null;
          }

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            _tokenExpiry: null,
            _refreshTokenExpiry: null
          });
        }
      },

      /**
       * Refresh access token using refresh token
       * Schedules next refresh automatically
       */
      refreshToken: async () => {
        const storedRefreshToken = localStorage.getItem('refreshToken');

        if (!storedRefreshToken) {
          // No refresh token available - logout user
          get().logout();
          return;
        }

        try {
          const response = await authService.refreshToken(storedRefreshToken);

          // Update access token
          localStorage.setItem('authToken', response.accessToken);

          // Calculate new expiry (assume 1 hour expiry since backend doesn't return it)
          const newExpiry = Date.now() + 60 * 60 * 1000;
          localStorage.setItem('tokenExpiry', newExpiry.toString());

          set({
            _tokenExpiry: newExpiry,
            error: null
          });

          // Schedule next refresh
          get().checkTokenExpiry();
        } catch (error) {
          // Token refresh failed - logout user
          console.error('Token refresh failed:', error);
          get().logout();
        }
      },

      /**
       * Check token expiry and schedule proactive refresh
       * Refreshes token 5 minutes before expiry
       * Validates expiry timestamp on app initialization
       */
      checkTokenExpiry: () => {
        const { _tokenExpiry } = get();

        // Clear existing timer
        if (refreshTimer) {
          clearTimeout(refreshTimer);
          refreshTimer = null;
        }

        if (!_tokenExpiry) return;

        const now = Date.now();
        const timeUntilExpiry = _tokenExpiry - now;
        const REFRESH_BEFORE_EXPIRY = 5 * 60 * 1000; // 5 minutes

        // If token expires in less than 5 minutes, refresh immediately
        if (timeUntilExpiry <= REFRESH_BEFORE_EXPIRY) {
          get().refreshToken();
          return;
        }

        // Schedule refresh 5 minutes before expiry
        const timeUntilRefresh = timeUntilExpiry - REFRESH_BEFORE_EXPIRY;
        refreshTimer = setTimeout(() => {
          get().refreshToken();
        }, timeUntilRefresh);
      },

      /**
       * Clear error message
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Update user data
       */
      updateUser: (updates: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } });
        }
      }
    }),
    {
      name: 'auth-storage', // localStorage key for persist
      partialize: (state) => ({
        // Only persist user and auth state, NOT transient states (loading, error)
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        _tokenExpiry: state._tokenExpiry,
        _refreshTokenExpiry: state._refreshTokenExpiry
      }),
      onRehydrateStorage: () => (state) => {
        // Called after state is rehydrated from localStorage
        if (state?.isAuthenticated && state._tokenExpiry) {
          const now = Date.now();

          // Validate token hasn't expired
          if (state._tokenExpiry <= now) {
            // Token expired - attempt refresh
            state.refreshToken();
          } else {
            // Token valid - schedule next refresh
            state.checkTokenExpiry();
          }
        }
      }
    }
  )
);
